<template>
  <section
    id="actualites"
    class="actus"
  >
    <div class="container">
      <SectionHead
        :icon="data.actualites.icon"
        :title="data.actualites.title"
        light
      />

      <div class="actus__grid">
        <article
          v-for="(item, i) in items"
          :key="i"
          class="actu-card"
          :class="{ 'is-open': openIndex === i, 'has-over': !!item.hoverText }"
          @click="toggleOpen(i)"
        >
          <a
            :href="item.href || '#'"
            class="actu-card__link"
            @click.prevent
          >
            <div class="actu-card__image">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.title"
              >
            </div>
            <div class="actu-card__body">
              <p class="actu-card__date">{{ item.date }}</p>
              <h3 class="actu-card__title">{{ item.title }}</h3>
              <p class="actu-card__text">{{ item.text }}</p>
            </div>
            <!-- volet du survol (maquette OVER) : monte depuis le filet dégradé, le
                 conteneur le découpe ; .is-open = équivalent tactile -->
            <div
              v-if="item.hoverText"
              class="actu-card__over-clip"
            >
              <div class="actu-card__over">
                <p class="actu-card__over-date">{{ item.hoverDate || item.date }}</p>
                <h3 class="actu-card__over-title">{{ item.hoverTitle || item.title }}</h3>
                <p
                  v-if="item.hoverSubtitle"
                  class="actu-card__over-text"
                >{{ item.hoverSubtitle }}</p>
                <p
                  v-for="(par, j) in paragraphs(item.hoverText)"
                  :key="j"
                  class="actu-card__over-text"
                  :class="{ 'is-spaced': par.spaced }"
                >{{ par.text }}</p>
              </div>
            </div>
            <!-- filet dégradé pleine largeur, puis flèche ; croix quand le panneau est
                 ouvert (cercle inclus dans les pictos) -->
            <div class="actu-card__foot">
              <img
                class="actu-card__icon is-arrow"
                src="/assets/pictos/ui/arrow.svg"
                alt=""
              >
              <img
                v-if="item.hoverText"
                class="actu-card__icon is-close"
                src="/assets/pictos/ui/close.svg"
                alt=""
              >
            </div>
          </a>
        </article>
      </div>
    </div>

    <!-- bandeau photo décoratif en boucle continue : la piste contient deux moitiés
         identiques et glisse d'une moitié, le raccord est donc invisible -->
    <div
      v-if="data.actualites.strip.length"
      class="actus__strip"
      aria-hidden="true"
    >
      <div
        class="actus__strip-track"
        :style="{ '--count': stripLoop.length / 2 }"
      >
        <img
          v-for="(img, i) in stripLoop"
          :key="i"
          :src="img"
          alt=""
        >
      </div>
    </div>
  </section>
</template>

<script>
import SectionHead from '@/components/SectionHead.vue'
import { scheduled } from '@/content/ccContent'

// vrai survol disponible (souris, pavé tactile) ; faux sur mobile/tablette
const canHover = () => window.matchMedia('(hover: hover)').matches

export default {
  name: 'ActualitesSection',
  components: { SectionHead },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // équivalent tactile du survol (mobile/tablette)
      openIndex: null
    }
  },
  computed: {
    // actualités en cours seulement ; les permanentes passent en dernier
    items() {
      return scheduled(this.data.actualites.items)
    },
    // Chaque moitié de la piste doit dépasser la largeur d'un grand écran (2 600 px,
    // tuiles de 278 px écart compris), sinon un vide apparaîtrait en bout de course :
    // la série est répétée autant que nécessaire, puis la moitié est doublée.
    stripLoop() {
      const photos = this.data.actualites.strip
      if (!photos.length) return []
      const repeat = Math.max(1, Math.ceil(2600 / (photos.length * 278)))
      const half = Array.from({ length: repeat }, () => photos).flat()
      return [...half, ...half]
    }
  },
  methods: {
    // écrans tactiles : 1er appui ouvre le survol, 2e appui le referme. À la
    // souris, le survol suffit (un clic ne doit pas le figer ouvert).
    toggleOpen(i) {
      if (canHover()) return
      this.openIndex = this.openIndex === i ? null : i
    },
    // une ligne vide = nouveau paragraphe ; deux lignes vides ou plus = paragraphe
    // précédé d'un espace plus grand (bloc Date / Horaires / Lieu de la maquette)
    paragraphs(text) {
      const parts = String(text || '').replace(/\r/g, '').split(/(\n[ \t]*\n(?:[ \t]*\n)*)/)
      const out = []
      for (let i = 0; i < parts.length; i += 2) {
        const par = parts[i].trim()
        if (!par) continue
        const sep = parts[i - 1] || ''
        out.push({ text: par, spaced: out.length > 0 && (sep.match(/\n/g) || []).length >= 3 })
      }
      return out
    }
  }
}
</script>

<style lang="scss" scoped>
// section marine avec le motif de courbes en filigrane. Le bandeau photo qui suit
// est posé SOUS la section (fond blanc, ~44 px de respiration), sans chevauchement.
.actus {
  background: $navy;
  padding: 64px 0 0;
  position: relative;
  overflow: hidden;

  &::before {
    @include curves-overlay($curves-dark);
  }

  .container {
    position: relative;
  }
}

.actus__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 40px;
}

.actu-card {
  // Dimensions relevées sur le PSD « actualités » (carte de 448 px de large) et
  // exprimées en % de la largeur de carte (cqw) : texte et panneau du survol
  // gardent les proportions de la maquette quelle que soit la taille de la carte.
  container-type: inline-size;
  background: $white;
  border-radius: 14px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 16px 30px rgba(#001B30, 0.35);
    }
  }

  &__link {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
  }

  // photo : 316 px de haut sur la maquette
  &__image {
    aspect-ratio: 448 / 316;
    background: #D9DFE5;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  // hauteur mini = celle de la maquette (283 px), pour que le texte du survol,
  // bien plus long, tienne dans la carte
  &__body {
    flex: 1;
    min-height: 63cqw;
    padding: 7.5cqw 6cqw 6cqw;
  }

  &__date {
    font-size: clamp(12px, 3.35cqw, 15px);
    font-weight: 700;
    color: $green;
  }

  &__title {
    margin-top: 1.2cqw;
    font-size: clamp(24px, 8.4cqw, 38px);
    font-weight: 700;
    color: $ink;
    line-height: 1.05;
    letter-spacing: -0.01em;
    white-space: pre-line;
  }

  &__text {
    margin-top: 4.5cqw;
    font-size: clamp(13px, 3.6cqw, 16px);
    line-height: 1.3;
    color: $ink;
    white-space: pre-line;
  }

  // filet dégradé vert -> bleu d'un bord à l'autre, picto en dessous à droite
  &__foot {
    --foot-h: 18.5cqw;
    position: relative;
    height: var(--foot-h);
    border-top: 3px solid transparent;
    border-image: linear-gradient(90deg, $green, $blue) 1;
  }

  &__icon {
    position: absolute;
    right: 4.5cqw;
    top: 50%;
    width: 10.5cqw;
    height: 10.5cqw;
    min-width: 34px;
    min-height: 34px;
    transform: translateY(-50%);
    transition: opacity 0.2s ease;

    &.is-close {
      opacity: 0;
    }
  }

  // Volet du survol : dégradé du PSD (#57A888 -> #1B8CAA), de haut en bas de la
  // carte jusqu'au filet inclus ; texte blanc centré. Il MONTE depuis le filet
  // dégradé (le conteneur découpe tout ce qui dépasse) et redescend à la sortie.
  // Défile si un texte saisi dans Strapi est plus long que la carte.
  &__over-clip {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    // jusqu'au bas du filet (3 px, compris dans la hauteur du pied)
    bottom: calc(18.5cqw - 3px);
    overflow: hidden;
    pointer-events: none;
  }

  &__over {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, #57A888, #1B8CAA);
    color: $white;
    padding: 9cqw 6.2cqw 6cqw;
    text-align: center;
    overflow-y: auto;
    scrollbar-width: thin;
    transform: translateY(100%);
    // caché une fois redescendu (pas de défilement ni de focus fantôme)
    visibility: hidden;
    transition: transform 0.45s cubic-bezier(0.22, 0.8, 0.3, 1), visibility 0s linear 0.45s;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    &-date {
      font-size: clamp(11px, 3.6cqw, 16px);
      font-weight: 700;
    }

    &-title {
      margin-top: 1.5cqw;
      font-size: clamp(22px, 8.4cqw, 38px);
      font-weight: 700;
      line-height: 1.05;
    }

    &-text {
      margin-top: 1.1em;
      font-size: clamp(12px, 3.9cqw, 17px);
      font-weight: 500;
      line-height: 1.16;
      white-space: pre-line;

      &.is-spaced {
        margin-top: 2.2em;
      }
    }
  }

  // :hover limité aux appareils qui survolent vraiment (sinon il reste collé
  // après un appui tactile et le 2e appui ne referme rien)
  &.is-open &__over {
    transform: none;
    visibility: visible;
    pointer-events: auto;
    transition-delay: 0s;
  }

  &.has-over.is-open &__icon.is-arrow {
    opacity: 0;
  }

  &.is-open &__icon.is-close {
    opacity: 1;
  }

  @media (hover: hover) {
    &:hover &__over {
      transform: none;
      visibility: visible;
      pointer-events: auto;
      transition-delay: 0s;
    }

    &:hover &__icon.is-close {
      opacity: 1;
    }

    &.has-over:hover &__icon.is-arrow {
      opacity: 0;
    }
  }
}

// Bandeau photo sous la section marine : vignettes carrées qui défilent seules,
// en boucle (maquette : 7 photos carrées bord à bord de l'écran). Pause au survol ;
// immobile si l'utilisateur a demandé moins d'animations.
.actus__strip {
  margin-top: 54px;
  background: $white;
  overflow: hidden;
  padding: 44px 0 0;
  // le filigrane (::before, positionné) passerait sinon par-dessus les photos
  position: relative;
  z-index: 1;

  --tile: 260px;
  --gap: 18px;
}

.actus__strip-track {
  display: flex;
  gap: var(--gap);
  width: max-content;
  // l'écart final fait de la piste deux séries identiques : -50 % = une série
  // exactement, la reprise de la boucle ne saute pas
  padding-right: var(--gap);
  animation: actus-strip calc(var(--count) * 6s) linear infinite;

  img {
    flex: 0 0 auto;
    width: var(--tile);
    height: var(--tile);
    object-fit: cover;
    border-radius: 14px;
  }

  &:hover {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
}

@keyframes actus-strip {
  to {
    transform: translateX(-50%);
  }
}

@include tablet-down {
  .actus__grid {
    grid-template-columns: 1fr;
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
  }
}

@include mobile {
  .actus {
    padding: 48px 0 0;
  }

  .actus__strip {
    margin-top: 40px;
    padding-top: 30px;
    --tile: 170px;
    --gap: 12px;
  }
}
</style>
